import * as React from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  styled as MUIStyled,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import styled from '@emotion/styled';
import { useModal } from 'mui-modal-provider';

import { AccessRequestAlert } from '../../components/Alerts/AccessRequestAlert';
import UserAvatarCategory from '../../components/Avatar/UserAvatarCategory/UserAvatarCategory';
import Button from '../../components/Button/Button';
import LoadingButton from '../../components/Button/LoadingButton';
import SaveUserButton from '../../components/Button/SaveUserButton';
import {
  AccessRequestAcceptedDialog,
  accessRequestPopupStorageKey,
} from '../../components/Dialogs/AccessRequestAccepted/AccessRequestAcceptedDialog';
import { IGStatsDialog } from '../../components/Dialogs/IGStats/IGStatsDialog';
import { IGTitle } from '../../components/Dialogs/IGStats/components/IGTitle';
import FloatTopLeft from '../../components/FloatTopLeft/FloatTopLeft';
import { CollabInviteWithoutReducer } from '../../components/Flows/CollabInvite/CollabInviteWithoutReducer';
import GoBack from '../../components/GoBack/GoBack';
import TrustedIcon from '../../components/Icons/TrustedIcon/TrustedIcon';
import InstagramTag, { InstaLink } from '../../components/InstagramTag/InstagramTag';
import { Gallery } from '../../components/Photoswipe/Photoswipe';
import { PremiumTooltip } from '../../components/PremiumTooltip/PremiumTooltip';
import SkeletonWithChild from '../../components/SkeletonWithChild/SkeletonWithChild';
import TabPanelContainer, { Panel } from '../../components/TabPanelContainer/TabPanelContainer';
import { useAction } from '../../components/Tables/AccessRequest/useAction';
import { useReservationLimitInPeriodContext } from '../../contexts/ReservationsLimitInPeriodContext';
import {
  User,
  UserAccessRequestResultValue,
  VennueAccessRequestItem,
  VenueAccessREnum,
} from '../../graphql/API';
import useAccessRequestQuery from '../../hooks/reactQuery/useAccessRequestQuery';
import { useQueryUser } from '../../hooks/reactQuery/useQueryUser';
import useQueryParams from '../../hooks/useQueryParams';
import useSubscriptionAccess from '../../hooks/useSubscriptionAccess';
import useVenueId from '../../hooks/useVenueId';
import { useAppSelector } from '../../redux/hooks';
import icons from '../../themes/icons';
import Icons from '../../themes/icons';
import { formatNumber } from '../../utils/formatter';
import { brandPromotionLimit } from '../../utils/reservations';
import { UserCategory } from '../../utils/user';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import { extractSubscriptionPlan } from '../SubscriptionPlans/common/helper';
import PanelUserContent from './components/PanelUserContent';
import PanelUserPortfolio from './components/PanelUserPortfolio';

type ProfileMetricCardProps = {
  loading: boolean;
  label: string;
  value: React.ReactNode;
  sx?: SxProps<Theme>;
};

const ProfileMetricCard: React.FC<ProfileMetricCardProps> = ({ loading, label, value, sx }) => (
  <SkeletonWithChild variant='rounded' loading={loading}>
    <Box minWidth='9rem'>
      <Box sx={{ ...sx }}>
        <Typography variant='h5' fontWeight='bold' minHeight={'42px'} textAlign={'left'}>
          {value}
        </Typography>
        <Typography sx={{ color: '#848484', textAlign: 'left' }}>{label}</Typography>
      </Box>
    </Box>
  </SkeletonWithChild>
);

const ProfileStack = MUIStyled(Stack)(({ theme }) => ({
  'div:not(:first-of-type)': {
    borderLeft: '1px solid #F1F1F1',
    paddingLeft: theme.spacing(2),
  },
}));

const UserProfile: React.FC = () => {
  const { getQueryParam } = useQueryParams();
  const offerID = getQueryParam('offerID');

  const actionProps = useAction();

  const subscriptionAccess = useSubscriptionAccess();
  const { subscription } = useAppSelector((state) => state.subscription.value);
  const { tags: venueTags } = useAppSelector((state) => state.venue.value) || {};

  const isOnlineBrand = venueTags?.includes(VENUE_MANUAL_TAGS.Online);

  const [inviteDialog, setInviteDialog] = React.useState(false);
  const [dontShowAccessRequestPopup, setDontShowAccessRequestPopup] = React.useState(false);
  const [selectedAccessRequest, setSelectedAccessRequest] =
    React.useState<VennueAccessRequestItem | null>(null);

  const { showModal } = useModal();

  const { userId } = useParams<{ userId: string }>();

  const venueId = useVenueId();
  const { data, isLoading } = useQueryUser(venueId, userId);
  const { data: accessRequests } = useAccessRequestQuery({
    status: UserAccessRequestResultValue.requested,
    userID: userId ? parseInt(userId, 10) : 0,
    offerID: offerID ? parseInt(offerID, 10) : 0,
  });
  const userData = React.useMemo(() => data ?? ({} as User), [data]);

  const { totalWaitingAndShippedReservations, totalReservedAndCheckedInReservations } =
    useReservationLimitInPeriodContext() || {};

  const panels = React.useMemo<Panel[]>(
    () => [
      { label: 'Portfolio', children: <PanelUserPortfolio userId={data?.id} /> },
      { label: 'Content', children: <PanelUserContent userId={data?.id} /> },
    ],
    [data?.id],
  );

  const handleAccessRequestSuccessPopup = () => {
    const showAccessRequestPopupValue = localStorage.getItem(accessRequestPopupStorageKey);
    const shouldShow = showAccessRequestPopupValue
      ? JSON.parse(showAccessRequestPopupValue)
      : false;
    setDontShowAccessRequestPopup(shouldShow);
  };

  React.useEffect(() => {
    handleAccessRequestSuccessPopup();
  }, []);

  const handleCreateInviteDialog = React.useCallback(async () => {
    if (!data) {
      return;
    }

    setInviteDialog((prev) => !prev);
  }, [data]);

  const handleIGStatsDialog = React.useCallback(() => {
    showModal(IGStatsDialog, {
      titleComponent: <IGTitle />,
      userID: userData.id,
    });
  }, [showModal, userData.id]);

  const fullProfile = React.useMemo(
    () => subscriptionAccess?.includes('COMPLETE_USER_PROFILES'),
    [subscriptionAccess],
  );

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const totalReservations = React.useMemo(
    () =>
      (isOnlineBrand
        ? totalWaitingAndShippedReservations
        : totalReservedAndCheckedInReservations) || 0,
    [isOnlineBrand, totalWaitingAndShippedReservations, totalReservedAndCheckedInReservations],
  );

  const isPromotionLimitHit = React.useMemo(
    () =>
      venueTags?.includes(VENUE_MANUAL_TAGS.BrandPlans) &&
      (subscriptionPlan === 'basic' || subscriptionPlan === 'starter') &&
      totalReservations >= brandPromotionLimit[subscriptionPlan],
    [subscriptionPlan, totalReservations, venueTags],
  );

  return (
    <>
      <Helmet>
        <title>{`Beautypass - ${userData.firstname} ${userData.lastname}`}</title>
      </Helmet>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          backgroundColor: 'white',
          flex: 1,
          py: inviteDialog ? 0 : 4,
          pl: inviteDialog ? 0 : 9,
          pr: inviteDialog ? 0 : 6,
        }}
      >
        {inviteDialog && data ? (
          <CollabInviteWithoutReducer users={[data]} onBack={handleCreateInviteDialog} />
        ) : (
          <>
            <FloatTopLeft>
              <GoBack />
            </FloatTopLeft>
            <Box
              sx={{
                display: 'flex',
                backgroundColor: '#FAFAFA',
                borderRadius: 1,
                px: 4,
                pt: 2,
                pb: 4,
                mb: 4,
              }}
            >
              {isLoading && <Skeleton variant='circular' width={'15rem'} height={'15rem'} />}
              {!isLoading && userData && (
                <UserAvatarCategory
                  showUserCategory={false}
                  variant='circular'
                  alt={userData.firstname}
                  placeholderSrc={String(userData.picture?.thumbnail)}
                  src={String(userData.picture?.medium)}
                  userCategory={userData.lifestyle ? UserCategory.lifestyle : UserCategory.select}
                  sx={{
                    width: '15rem',
                    height: '15rem',
                  }}
                />
              )}
              <Box
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'space-between'}
                sx={{ width: '100%', ml: 3, py: 1 }}
              >
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography component='div' sx={{ display: 'flex', alignItems: 'center' }}>
                      <SkeletonWithChild width='100%' loading={isLoading}>
                        <Typography fontWeight={600} fontSize={'26px'}>
                          {userData.firstname} {userData.lastname}
                        </Typography>
                        {userData.agencyProfileUrl && (
                          <TrustedIcon sx={{ fontSize: '1.2rem', ml: 1 }} />
                        )}
                      </SkeletonWithChild>
                    </Typography>

                    <SkeletonWithChild width='80%' loading={isLoading}>
                      <Typography
                        variant='subtitle1'
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      >
                        {fullProfile && userData.instagramHandle && !isPromotionLimitHit && (
                          <InstagramBox>
                            <Icons.InstagramIcon />
                            <InstagramTag
                              username={userData.instagramHandle}
                              sx={{ mr: 1 }}
                              textColor='#1E55A1'
                            />
                          </InstagramBox>
                        )}

                        {data?.agencyProfileUrl && data?.agency && (
                          <InstagramBox>
                            <InstaLink
                              component='a'
                              target='_blank'
                              href={data?.agencyProfileUrl || ''}
                              textColor={'#1E55A1'}
                              fontWeight={500}
                            >
                              {data?.agency}
                            </InstaLink>
                          </InstagramBox>
                        )}
                      </Typography>
                    </SkeletonWithChild>
                  </Box>
                  <Stack spacing={4} direction='row' alignItems='flex-start'>
                    <SaveUserButton
                      size='large'
                      userId={Number(userData.id)}
                      saved={Boolean(userData.savedByVenue)}
                      isOnlineBrand
                    ></SaveUserButton>

                    {subscriptionPlan === 'starter' || subscriptionPlan === 'plus' ? (
                      <Button
                        color='secondary'
                        variant='contained'
                        size='large'
                        onClick={handleIGStatsDialog}
                        sx={{ px: 4, color: '#000000', backgroundColor: '#ececec' }}
                        startIcon={<icons.StatsIcon />}
                      >
                        Analytics
                      </Button>
                    ) : (
                      <PremiumTooltip showStarterIcon>
                        <Button
                          color='secondary'
                          variant='contained'
                          size='large'
                          onClick={handleIGStatsDialog}
                          sx={{
                            px: 4,
                            color: '#000000',
                            backgroundColor: '#ececec',
                            '&:disabled': {
                              color: '#000000',
                            },
                          }}
                          startIcon={<icons.StatsIcon />}
                          disabled
                        >
                          Analytics
                        </Button>
                      </PremiumTooltip>
                    )}

                    {!offerID ? (
                      <>
                        {!data?.isEventReserved && (
                          <Button
                            variant='contained'
                            size='large'
                            sx={{ px: 4 }}
                            onClick={handleCreateInviteDialog}
                          >
                            {userData ? `Invite ${userData?.firstname}` : 'Invite'}
                          </Button>
                        )}
                      </>
                    ) : (
                      <Stack spacing={1} direction='row'>
                        <LoadingButton
                          fullWidth
                          variant='contained'
                          onClick={() => {
                            if (offerID) {
                              if (accessRequests.length)
                                setSelectedAccessRequest(accessRequests[0]);
                              actionProps.onAction({
                                action: VenueAccessREnum.accepted,
                                offerID: parseInt(offerID, 10),
                                userID: parseInt(userData.id, 10),
                              });
                            }
                          }}
                          loading={
                            actionProps.loadingState[
                              `${userData?.id}:${offerID}:${VenueAccessREnum.accepted}`
                            ]
                          }
                          disabled={
                            actionProps.loadingState[
                              `${userData?.id}:${offerID}:${VenueAccessREnum.accepted}`
                            ] || actionProps.successState
                          }
                          sx={{ py: 1 }}
                        >
                          Accept
                        </LoadingButton>
                        <LoadingButton
                          fullWidth
                          variant='outlined'
                          onClick={() => {
                            if (offerID) {
                              if (accessRequests.length) setSelectedAccessRequest(null);
                              actionProps.onAction({
                                action: VenueAccessREnum.declined,
                                offerID: parseInt(offerID, 10),
                                userID: parseInt(userData.id, 10),
                              });
                            }
                          }}
                          loading={
                            actionProps.loadingState[
                              `${userData?.id}:${offerID}:${VenueAccessREnum.declined}`
                            ]
                          }
                          disabled={
                            actionProps.loadingState[
                              `${userData?.id}:${offerID}:${VenueAccessREnum.declined}`
                            ] || actionProps.successState
                          }
                          sx={{ py: 1 }}
                        >
                          Reject
                        </LoadingButton>
                      </Stack>
                    )}
                  </Stack>
                </Box>
                <Box>
                  <ProfileStack spacing={4} direction='row'>
                    <ProfileMetricCard
                      loading={isLoading}
                      label={'Average rating'}
                      value={
                        <Typography
                          component='div'
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            justifyContent: 'flex-start',
                          }}
                        >
                          {(userData?.rating || 0) > 0 ? (
                            <>
                              <StarIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />
                              <Box component='span' fontWeight='bold' fontSize={'28px'}>
                                {userData?.rating?.toFixed(1)}
                              </Box>
                              <Typography component={'span'} color='#000' mx={0.5}>
                                ({userData.totalRatings})
                              </Typography>
                            </>
                          ) : (
                            <Typography component={'span'} color='#000' mx={0.5}>
                              no ratings yet
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                    <ProfileMetricCard
                      loading={isLoading}
                      label={'Followers'}
                      value={formatNumber(userData.instagramFollowers ?? 0)}
                      sx={{ pl: 2 }}
                    />
                    <ProfileMetricCard
                      loading={isLoading}
                      label={'Total promotions'}
                      value={data?.totalRequirementsSubmitted}
                      sx={{ pl: 2 }}
                    />
                    <ProfileMetricCard
                      loading={isLoading}
                      label={'Market'}
                      value={userData?.marketName}
                      sx={{ pl: 2 }}
                    />
                  </ProfileStack>
                </Box>
              </Box>
            </Box>

            <Gallery>
              <TabPanelContainer panels={panels} defaultValue={(data?.totalPortfolioContents || 0) > 0 ? 0 : (data?.totalContentUploaded || 0) > 0 ? 1 : 0} />
            </Gallery>
          </>
        )}
      </Box>
      <AccessRequestAlert
        open={
          ((selectedAccessRequest || false) &&
            actionProps.successState &&
            isOnlineBrand &&
            !!offerID &&
            dontShowAccessRequestPopup) ||
          false
        }
        onClose={() => {
          setSelectedAccessRequest(null);
          actionProps.setSuccessState(false);
        }}
        accessRequest={selectedAccessRequest}
      />
      {!dontShowAccessRequestPopup && (
        <AccessRequestAcceptedDialog
          open={
            ((selectedAccessRequest || false) &&
              actionProps.successState &&
              isOnlineBrand &&
              !dontShowAccessRequestPopup &&
              !!offerID) ||
            false
          }
          accessRequest={selectedAccessRequest}
          onClose={() => {
            actionProps.setSuccessState(false);
            setSelectedAccessRequest(null);
          }}
          reservationID={actionProps.reservationID}
        />
      )}
    </>
  );
};

export default UserProfile;

const InstagramBox = styled.div`
  display: inline-flex;
  padding: 2px 8px;
  align-items: center;
  gap: 10px;
  border-radius: 6px;
  background-color: #ececec;
`;
